import React from "react";

/**
 * ErrorBoundary — top-level React error boundary that catches any
 * unhandled render error in the app tree and shows a friendly recovery
 * screen instead of the default blank white page.
 *
 * Wraps the entire Route tree in `App.js`. Class-component pattern is
 * required because React still doesn't have a hook form of
 * `componentDidCatch` (as of Feb 2026).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // Preserve the full trace in the console so devs can debug in
    // production without redeploying. Also try to POST a lightweight
    // client-error crumb to the backend if the endpoint exists —
    // fire-and-forget, never blocks recovery.
    console.error("[ErrorBoundary]", error, info?.componentStack);
    try {
      fetch("/api/client-errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: (info?.componentStack || "").slice(0, 4000),
          url: typeof window !== "undefined" ? window.location.href : "",
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
          at: new Date().toISOString(),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* offline / CSP */ }
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div data-testid="error-boundary" className="min-h-screen flex items-center justify-center bg-[#050810] px-4">
        <div className="max-w-md text-center text-white">
          <div className="text-tv-accent text-[10px] uppercase tracking-[0.4em] font-black mb-2">Something broke</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">
            That&apos;s on us — not you.
          </h1>
          <p className="text-white/60 text-sm mb-6">
            The page hit an unexpected error. We&apos;ve logged what happened. Try refreshing, or head back home.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              type="button"
              onClick={() => { window.location.reload(); }}
              data-testid="error-boundary-reload"
              className="bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs uppercase tracking-wider px-5 py-2.5 rounded-full"
            >Reload page</button>
            <a
              href="/"
              data-testid="error-boundary-home"
              className="bg-white/5 hover:bg-white/10 text-white font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-full border border-white/10 inline-flex items-center justify-center"
            >Back to home</a>
          </div>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="text-left mt-6 text-[10px] text-white/40 bg-white/[0.03] rounded-lg p-3 overflow-auto max-h-40">
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
